<script lang="ts" setup>
import dayjs from 'dayjs'
import type { ContentTypeArticle } from '~~/types/storyblok/contentTypes'
import { usePageMetaHelper } from '~~/composables/usePageMetaHelper'

const props = defineProps<{
  blok: ContentTypeArticle
  title: string
  dateUpdated?: string
  datePublished?: string
}>()

const res = await useAsyncStoryblokDatasourceQuery({
  datasource: 'article-categories',
})

const categoryOptions = computed(() => {
  return res.value.data.datasource_entries.map((category) => ({
    label: category.name,
    value: category.value,
  }))
})

const heroImage = computed(() => {
  return {
    _uid: 'hero',
    component: 'MediaImage',
    asset: props?.blok?.hero || '',
  }
})

const categorySlugs = computed(() => {
  return props?.blok?.category?.join(',')
})

const category = computed(() => {
  // categoryList
  const categoryValue = props?.blok?.category?.[0]

  if (!categoryValue) {
    return {}
  }

  const categoryOption = categoryOptions.value.filter(
    (option) => option.value === categoryValue
  )[0]

  return {
    name: categoryOption.label,
    slug: categoryValue,
  }
})

const publishedShort = computed(() => {
  return !!props?.datePublished
    ? dayjs(props.datePublished).format('MMMM YYYY')
    : ''
})
const publishedLong = computed(() => {
  return !!props?.datePublished
    ? dayjs(props.datePublished).format('D MMMM YYYY')
    : ''
})

// SEO settings
const pageTitle = props.blok?.seo?.title || props.title || ''
const pageDescription =
  props.blok?.seo?.description || props.blok?.introduction || ''
const pageImage =
  props.blok?.seo_image?.filename || props?.blok?.hero?.filename || ''

const { applyMeta } = usePageMetaHelper(useRoute())
applyMeta({
  name: `${props.title} - Insights`,
  title: pageTitle,
  description: pageDescription,
  image: pageImage,
})
</script>

<template lang="pug">
article(v-editable="blok" class="content-article text-md flex flex-col items-center")
  header(class="pt-120 md:pt-144 flex flex-col items-center container")
    div(class="max-w-prose px-18 md:px-0")
      PageBreadcrumbs(
        :parent-pages="[{ label: 'Insights', value: '/insights' }]"
      )
      h1 {{ props.title }}
      p(class="text-md font-medium md:text-lg md:font-normal") {{ props?.blok?.introduction }}

      div(class="article-meta flex justify-between items-center gap-18 mt-36")
        div(
          v-if="props?.datePublished"
          class="flex items-center gap-6 text-sm font-medium text-grey-500"
        )
          MaterialSymbol calendar_today
          p(class="pt-3 m-0")
            span(class="md:hidden") {{ publishedShort }}
            span(class="hidden md:inline") Published {{ publishedLong }}
        NuxtLink(
          v-if="category?.name"
          :to="`/insights?category=${category.slug}`"
          class="inline-flex px-9 py-3 text-sm font-medium text-blue-dark border-2 border-highlight rounded-full"
        ) {{ category.name }}
  div(class="container hero-image mt-24 xl:mb-48 w-full max-w-[1200px]" v-if="heroImage")
    StoryblokComponent(
      :blok="heroImage"
      :width="1200"
      :height="540"
      :object-cover="true"
      aspect="article-hero"
    )

  div(
    class="w-full bg-grey-200 flex flex-col items-center py-48 xl:pt-180 xl:-mt-144 xl:pb-144"
  )
    StoryblokComponent(
      v-for="item in blok?.body"
      :key="item._uid"
      :blok="item"
    )

    hr(class="mx-auto")

  ArticleRelatedShowcase(:category-slugs="categorySlugs")
</template>

<style lang="postcss" scoped>
.blok-rich-text {
  @apply max-w-prose mb-24 px-18 md:px-0;

  & + & {
    @apply mt-36;
  }
}

.blok-media {
  @apply max-w-prose-widest mt-24 mb-48;
}

.blok-section {
  @apply max-w-screen-xl;
  & + & {
    @apply mt-36;
  }
}
</style>
